import { useMemo, useState, useRef, useEffect, type CSSProperties } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import {
  api,
  ApiError,
  type SourceStateResponse,
  type OrgSource,
  type ImportFolder,
  type ImportCandidate,
  type ManagedReader,
  type ProjectResponse,
  type SourceRecommendation,
  type SourceDiscoveryJobResponse,
  type UserbotsResponse,
} from '@/lib/api'
import { Select } from '@/components/Select'
import { HistoryScanControl } from '@/components/HistoryScanControl'
import { UpsellNotice } from '@/components/UpsellNotice'
import { ConnectUserbotModal } from '@/components/ConnectUserbotModal'
import { buildProjectNumberMap, projectLabel, projectLabelById } from '@/lib/projectLabels'
import { formatUserbotSelectionError } from '@/lib/userbotErrors'
import { useAuth } from '@/lib/auth'

type OrgSourceSort =
  | 'leads_desc'
  | 'leads_asc'
  | 'last_desc'
  | 'last_asc'
  | 'yield_desc'
  | 'noise_desc'
type OrgSourceFilter = 'all' | 'good' | 'noisy' | 'quiet' | 'access_error' | 'new'
type ProjectSourcesTab = 'sources' | 'recommendations'
type SourceRecommendationsMessage = { text: string; error: boolean }

function parseSourceStateIdsParam(raw: string | null): number[] {
  if (!raw) return []
  const seen = new Set<number>()
  return raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => {
      if (!Number.isFinite(id) || id <= 0 || seen.has(id)) return false
      seen.add(id)
      return true
    })
}

function formatSourceDiscoveryJobMessage(
  job: SourceDiscoveryJobResponse | null | undefined,
): SourceRecommendationsMessage | null {
  if (!job) return null
  if (job.status === 'failed') {
    return { text: job.error || 'Не удалось выполнить подбор похожих источников', error: true }
  }
  if (job.status !== 'done') return null

  const result = (job.result ?? {}) as {
    requests?: number
    telegram_edges?: number
    overlap_edges?: number
    semantic_match_edges?: number
    linked_source_edges?: number
    geo_match_edges?: number
    text_profile_edges?: number
    enrichment_attempted?: number
    enrichment_processed?: number
    enrichment_enriched?: number
    enrichment_errors?: number
    enrichment_skipped?: number
    skipped_recent?: number
    assigned_total?: number
    readable_total?: number
    failed?: string[]
  }

  if (job.kind === 'add') {
    const failed = result.failed ?? []
    return {
      text:
        failed.length > 0
          ? `Не добавлено: ${failed.join(', ')}`
          : `Добавлено: ${result.assigned_total ?? 0}, читаемых: ${result.readable_total ?? 0}`,
      error: failed.length > 0,
    }
  }

  const requests = result.requests ?? 0
  const telegramEdges = result.telegram_edges ?? 0
  const overlapEdges = result.overlap_edges ?? 0
  const semanticEdges = result.semantic_match_edges ?? 0
  const linkedEdges = result.linked_source_edges ?? 0
  const geoEdges = result.geo_match_edges ?? 0
  const textEdges = result.text_profile_edges ?? 0
  const enrichmentAttempted = result.enrichment_attempted ?? 0
  const enrichmentProcessed = result.enrichment_processed ?? result.enrichment_enriched ?? 0
  const enrichmentEnriched = result.enrichment_enriched ?? 0
  const enrichmentErrors = result.enrichment_errors ?? 0
  const skippedRecent = result.skipped_recent ?? 0
  const enrichmentPart =
    enrichmentAttempted > 0
      ? `, ${enrichmentProcessed}/${enrichmentAttempted} источников обработано AI, ${enrichmentEnriched} обновлено`
      : ''
  const enrichmentErrorPart =
    enrichmentErrors > 0 ? ', гео/тема не разобраны из-за ошибки AI' : ''
  const skippedPart = skippedRecent > 0 ? `, ${skippedRecent} недавно проверенных источников пропущено` : ''
  return {
    text: `Последняя проверка: ${requests} запросов Telegram, ${semanticEdges} по смыслу, ${telegramEdges} Telegram-рекомендаций, ${linkedEdges} связанных, ${geoEdges} гео, ${textEdges} по профилю, ${overlapEdges} пересечений${enrichmentPart}${enrichmentErrorPart}${skippedPart}`,
    error: false,
  }
}

// "Мало данных" (the `new` bucket) is intentionally not a chip here — it is
// replaced by the horizontal "Минимум лидов" slider in the toolbar, which lets
// the user dial in how many leads a source must have to stay visible.
const ORG_SOURCE_FILTERS: Array<{ key: OrgSourceFilter; label: string }> = [
  { key: 'all', label: 'Все' },
  { key: 'good', label: 'Хорошие' },
  { key: 'noisy', label: 'Шумные' },
  { key: 'quiet', label: 'Тихие' },
  { key: 'access_error', label: 'Ошибки доступа' },
]

function sourceQualityBucket(source: OrgSource): OrgSourceFilter {
  if (source.quality_label === 'pending') return 'new'
  if (source.quality_label === 'active') return 'good'
  return source.quality_label
}

function sourceQualityText(label: OrgSource['quality_label']) {
  const labels: Record<OrgSource['quality_label'], string> = {
    good: 'Хороший источник',
    noisy: 'Много шума',
    quiet: 'Тихий источник',
    access_error: 'Ошибка доступа',
    new: 'Мало данных',
    pending: 'Проверяется',
    active: 'Есть лиды',
  }
  return labels[label] ?? 'Мало данных'
}

function scorePercent(value: number | null | undefined) {
  return Math.max(0, Math.min(100, Math.round((value ?? 0) * 100)))
}

function sourceInitials(title: string | null, chatId: number) {
  const source = (title || String(chatId)).trim()
  const words = source.split(/\s+/).filter(Boolean)
  const letters = words
    .map((word) => Array.from(word).find((char) => /[\p{L}\p{N}]/u.test(char)))
    .filter(Boolean)
  if (letters.length >= 2) return `${letters[0]}${letters[1]}`.toUpperCase()
  if (letters.length === 1) {
    const tail = Array.from(source)
      .filter((char) => /[\p{L}\p{N}]/u.test(char))
      .slice(1, 2)
    return `${letters[0]}${tail[0] ?? ''}`.toUpperCase()
  }
  return Array.from(source).slice(0, 2).join('').toUpperCase()
}

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleDateString('ru-RU') : '—'
}

function telegramChatUrl(source: {
  chat_id: number
  username?: string | null
  source_url?: string | null
}): string | null {
  if (source.source_url) return source.source_url
  if (source.username) return `https://t.me/${source.username.replace(/^@/, '')}`
  // Private supergroup/channel without a public @username: Telegram exposes a
  // deep link at t.me/c/<internal id>, where the internal id is the chat_id
  // with its -100 prefix stripped. Works for members of the chat.
  const idStr = String(source.chat_id)
  if (idStr.startsWith('-100')) {
    const internal = idStr.slice(4)
    if (internal) return `https://t.me/c/${internal}`
  }
  return null
}

function sourceAcquisitionText(method: string | null | undefined) {
  if (method === 'source_recommendation') return 'Подобран'
  if (method === 'file_import') return 'Импорт списка'
  return 'Из Telegram'
}

function sourceAcquisitionStyle(method: string | null | undefined): CSSProperties {
  if (method === 'source_recommendation') {
    return {
      background: 'var(--accent-soft)',
      border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)',
      color: 'var(--accent-ink)',
    }
  }
  if (method === 'file_import') {
    return {
      background: 'rgba(11,15,20,0.04)',
      border: '1px solid var(--border)',
      color: 'var(--ink-2)',
    }
  }
  return {
    background: 'var(--teal-soft)',
    border: '1px solid color-mix(in oklab, var(--teal) 30%, transparent)',
    color: 'var(--teal-ink)',
  }
}

function sourceChannelText(source: SourceStateResponse) {
  if (source.source_origin === 'sensor_managed') return 'SENSOR'
  return sourceAcquisitionText(source.acquisition_method)
}

function sourceChannelStyle(source: SourceStateResponse): CSSProperties {
  if (source.source_origin === 'sensor_managed') {
    return {
      background: 'var(--teal-soft)',
      border: '1px solid color-mix(in oklab, var(--teal) 35%, transparent)',
      color: 'var(--teal-ink)',
    }
  }
  return sourceAcquisitionStyle(source.acquisition_method)
}

function numericChatId(value: unknown): number {
  return Number(value)
}

function chatIdKey(value: unknown): string {
  return String(value).trim()
}

function ScoreBar({
  value,
  isNoise,
  label,
}: {
  value: number
  isNoise?: boolean
  label: string
}) {
  const pct = Math.min(100, Math.round((value ?? 0) * 100))
  return (
    <div className="space-y-0.5">
      <div className="flex justify-between text-xs" style={{ color: 'var(--muted)' }}>
        <span>{label}</span>
        <span>{pct}</span>
      </div>
      <div
        className="h-1.5 w-full rounded-full"
        style={{ background: 'rgba(11,15,20,0.06)' }}
      >
        <div
          className={`h-full rounded-full ${isNoise ? 'bg-red-300' : ''}`}
          style={{
            width: `${pct}%`,
            ...(isNoise ? {} : { background: 'var(--teal)' }),
          }}
        />
      </div>
    </div>
  )
}

function AccessBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs" style={{ color: 'var(--muted)' }}>—</span>
  if (status === 'ok') {
    return (
      <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700">
        {status}
      </span>
    )
  }
  if (status === 'no_access' || status === 'kick' || status === 'banned') {
    return (
      <span className="rounded-full px-2 py-0.5 text-xs font-medium bg-red-50 text-red-600">
        {status}
      </span>
    )
  }
  return (
    <span
      className="rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: 'rgba(11,15,20,0.04)', color: 'var(--ink-2)' }}
    >
      {status}
    </span>
  )
}

function SourceRow({
  source,
  selected,
  onClick,
}: {
  source: SourceStateResponse
  selected: boolean
  onClick: () => void
}) {
  const total = source.stored_leads_count
  const likedPct = total > 0 ? Math.round((source.liked_leads_count / total) * 100) : 0
  const dislikedPct = total > 0 ? Math.round((source.disliked_leads_count / total) * 100) : 0
  const sourceUrl = telegramChatUrl(source)
  const readerLabel =
    source.source_origin === 'sensor_managed'
      ? 'Источник от SENSOR'
      : source.reader_label

  return (
    <tr
      onClick={onClick}
      className="cursor-pointer transition-colors"
      style={{
        borderTop: '1px solid var(--border)',
        background: selected ? 'oklch(0.97 0.02 195)' : undefined,
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLTableRowElement).style.background = 'rgba(11,15,20,0.03)'
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLTableRowElement).style.background = ''
        }
      }}
    >
      <td className="px-4 py-3">
        {sourceUrl ? (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-sm hover:underline"
            style={{ color: 'var(--ink)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {source.group_title ?? `ID ${source.chat_id}`}
          </a>
        ) : (
          <p className="font-medium text-sm" style={{ color: 'var(--ink)' }}>
            {source.group_title ?? `ID ${source.chat_id}`}
          </p>
        )}
        {source.removed_at && (
          <p className="text-xs text-red-400 mt-0.5">Удалён</p>
        )}
        {readerLabel && (
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
            {readerLabel}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        <span
          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
          style={sourceChannelStyle(source)}
        >
          {sourceChannelText(source)}
        </span>
      </td>
      <td className="px-4 py-3">
        <AccessBadge status={source.last_access_status} />
      </td>
      <td className="px-4 py-3 text-sm text-right font-medium" style={{ color: 'var(--ink-2)' }}>
        {total}
      </td>
      <td className="px-4 py-3 text-xs text-right whitespace-nowrap" style={{ color: 'var(--ink-2)' }}>
        <span className="text-green-600">+{likedPct}%</span>
        {' / '}
        <span className="text-red-500">−{dislikedPct}%</span>
      </td>
      <td className="px-4 py-3 w-28">
        <div className="space-y-1">
          <ScoreBar
            value={source.source_yield_score ?? 0}
            label="Yield"
          />
          <ScoreBar
            value={source.source_noise_score ?? 0}
            isNoise
            label="Noise"
          />
        </div>
      </td>
      <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--muted)' }}>
        {source.last_lead_at
          ? new Date(source.last_lead_at).toLocaleDateString('ru-RU')
          : '—'}
      </td>
    </tr>
  )
}

function SourceDetail({
  source,
  projectId,
  canRemove = true,
  onClose,
  onRemoved,
}: {
  source: SourceStateResponse
  projectId: number
  canRemove?: boolean
  onClose: () => void
  onRemoved?: () => void
}) {
  const queryClient = useQueryClient()
  const removeMutation = useMutation({
    mutationFn: () => api.removeSource(projectId, source.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      onRemoved?.()
      onClose()
    },
  })

  function handleRemove() {
    if (!window.confirm('Убрать источник из проекта?')) return
    removeMutation.mutate()
  }

  const accessReason = source.last_access_reason
  const leadsHref = `/leads?project=${projectId}${
    source.username ? `&source=${encodeURIComponent(source.username.replace(/^@/, ''))}` : ''
  }`

  return (
    <aside
      className="fixed inset-y-0 right-0 z-20 flex w-[380px] max-w-full flex-col shadow-xl"
      style={{
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <span className="text-sm font-semibold truncate" style={{ color: 'var(--ink-2)' }}>
          {source.group_title ?? `Источник ${source.id}`}
        </span>
        <button
          onClick={onClose}
          className="rounded p-1"
          style={{ color: 'var(--muted)' }}
          onMouseEnter={(e) => {
            const btn = e.currentTarget as HTMLButtonElement
            btn.style.color = 'var(--ink)'
            btn.style.background = 'rgba(11,15,20,0.04)'
          }}
          onMouseLeave={(e) => {
            const btn = e.currentTarget as HTMLButtonElement
            btn.style.color = 'var(--muted)'
            btn.style.background = ''
          }}
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5 text-sm">
        <div className="grid grid-cols-2 gap-3">
          {[
            ['Лидов всего', source.stored_leads_count],
            ['Горячих', source.live_leads_count],
            ['История', source.history_leads_count],
            ['Лайков', source.liked_leads_count],
            ['Дизлайков', source.disliked_leads_count],
            ['Не уверен', source.unsure_leads_count],
          ].map(([label, val]) => (
            <div
              key={label as string}
              className="rounded-lg p-3"
              style={{ background: 'rgba(11,15,20,0.03)' }}
            >
              <p className="text-xs" style={{ color: 'var(--muted)' }}>{label as string}</p>
              <p className="text-lg font-bold" style={{ color: 'var(--ink)' }}>{val as number}</p>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <ScoreBar
            value={source.source_yield_score ?? 0}
            label="Yield score"
          />
          <ScoreBar
            value={source.source_noise_score ?? 0}
            isNoise
            label="Noise score"
          />
        </div>

        <div className="space-y-1 text-xs" style={{ color: 'var(--muted)' }}>
          <p>
            Канал:{' '}
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 font-medium"
              style={sourceChannelStyle(source)}
            >
              {sourceChannelText(source)}
            </span>
          </p>
          <p>
            Доступ:{' '}
            <AccessBadge status={source.last_access_status} />
          </p>
          {accessReason && (
            <p>
              Причина:{' '}
              <span style={{ color: 'var(--ink-2)' }}>{accessReason}</span>
            </p>
          )}
          {source.last_scan_at && (
            <p>
              Последнее сканирование:{' '}
              <span style={{ color: 'var(--ink-2)' }}>
                {new Date(source.last_scan_at).toLocaleString('ru-RU')}
              </span>
            </p>
          )}
          {source.last_lead_at && (
            <p>
              Последний лид:{' '}
              <span style={{ color: 'var(--ink-2)' }}>
                {new Date(source.last_lead_at).toLocaleString('ru-RU')}
              </span>
            </p>
          )}
          {source.removed_at && (
            <p className="text-red-400">
              Удалён:{' '}
              {new Date(source.removed_at).toLocaleString('ru-RU')}
            </p>
          )}
        </div>

        <a
          href={leadsHref}
          className="btn btn--ghost btn--sm inline-flex items-center gap-1.5"
        >
          Смотреть лиды →
        </a>

        {canRemove && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: '12px' }}>
          {removeMutation.isError && (
            <p className="text-xs text-red-500 mb-2">
              {removeMutation.error instanceof ApiError
                ? removeMutation.error.message
                : 'Ошибка при удалении'}
            </p>
          )}
          <button
            onClick={handleRemove}
            disabled={removeMutation.isPending}
            className="btn btn--sm"
            style={{
              background: 'var(--danger-soft, #fff1f0)',
              color: 'var(--danger-ink, #c0392b)',
              border: '1px solid var(--danger-line, #fca5a5)',
              opacity: removeMutation.isPending ? 0.6 : 1,
            }}
          >
            {removeMutation.isPending ? 'Удаление...' : 'Удалить источник'}
          </button>
        </div>
        )}
      </div>
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Import Drawer (project-scoped)
// ---------------------------------------------------------------------------

function Spinner() {
  return (
    <div
      className="h-4 w-4 animate-spin rounded-full flex-shrink-0"
      style={{ border: '2px solid var(--teal)', borderTopColor: 'transparent' }}
    />
  )
}

function ImportDrawer({
  projectId,
  onClose,
}: {
  projectId: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined)
  const [selectedChatIds, setSelectedChatIds] = useState<Set<number>>(new Set())
  const [importResult, setImportResult] = useState<string | null>(null)
  const refreshedRef = useRef(false)

  // Kick off a refresh once when the drawer opens
  const refreshMutation = useMutation({
    mutationFn: () => api.refreshImport(projectId),
  })

  useEffect(() => {
    if (!refreshedRef.current) {
      refreshedRef.current = true
      refreshMutation.mutate()
    }
  }, [])

  // Poll folders until status is ready/failed/no_userbot
  const foldersQuery = useQuery({
    queryKey: ['import-folders', projectId],
    queryFn: () => api.getImportFolders(projectId),
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'requested' || status === 'none' || status === undefined) return 2000
      return false
    },
  })

  const status = foldersQuery.data?.status ?? 'none'
  const folders: ImportFolder[] = foldersQuery.data?.folders ?? []

  // Load candidates when folder selection changes and status is ready
  const candidatesQuery = useQuery({
    queryKey: ['import-candidates', projectId, selectedFolderId],
    queryFn: () => api.getImportCandidates(projectId, selectedFolderId),
    enabled: status === 'ready',
  })

  const candidates: ImportCandidate[] = candidatesQuery.data?.candidates ?? []
  const availableCandidates = candidates.filter((c) => !c.already_added)

  function toggleCandidate(chatId: number) {
    setSelectedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(chatId)) next.delete(chatId)
      else next.add(chatId)
      return next
    })
  }

  function toggleAll() {
    const ids = availableCandidates.map((c) => c.chat_id)
    const allSelected = ids.every((id) => selectedChatIds.has(id))
    if (allSelected) {
      setSelectedChatIds(new Set())
    } else {
      setSelectedChatIds(new Set(ids))
    }
  }

  const applyMutation = useMutation({
    mutationFn: () => api.applyImport(projectId, Array.from(selectedChatIds)),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      void queryClient.invalidateQueries({ queryKey: ['import-candidates', projectId] })
      setSelectedChatIds(new Set())
      setImportResult(
        `Добавлено: ${result.added}, уже были: ${result.skipped_existing}, сверх лимита: ${result.skipped_by_limit}`,
      )
    },
  })

  function handleRetry() {
    void queryClient.invalidateQueries({ queryKey: ['import-folders', projectId] })
    refreshMutation.mutate()
  }

  const allSelected =
    availableCandidates.length > 0 &&
    availableCandidates.every((c) => selectedChatIds.has(c.chat_id))

  return (
    <aside
      className="fixed inset-y-0 right-0 z-30 flex flex-col shadow-xl"
      style={{
        width: '440px',
        maxWidth: '100vw',
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          Импорт из папок Telegram
        </span>
        <button
          onClick={onClose}
          className="rounded p-1"
          style={{ color: 'var(--muted)' }}
          onMouseEnter={(e) => {
            const btn = e.currentTarget as HTMLButtonElement
            btn.style.color = 'var(--ink)'
            btn.style.background = 'rgba(11,15,20,0.04)'
          }}
          onMouseLeave={(e) => {
            const btn = e.currentTarget as HTMLButtonElement
            btn.style.color = 'var(--muted)'
            btn.style.background = ''
          }}
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
        {/* Status: no_userbot */}
        {status === 'no_userbot' && (
          <div
            className="rounded-lg p-4 text-sm"
            style={{ background: 'rgba(11,15,20,0.04)', color: 'var(--muted)' }}
          >
            Сначала привяжите юзербот к проекту (вкладка проекта).
          </div>
        )}

        {/* Status: loading / syncing */}
        {(status === 'requested' || status === 'none') && (
          <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
            <Spinner />
            <span>Синхронизирую диалоги Telegram…</span>
          </div>
        )}

        {/* Status: failed */}
        {status === 'failed' && (
          <div className="space-y-3">
            <p className="text-red-500">Не удалось синхронизировать диалоги.</p>
            <button
              onClick={handleRetry}
              className="btn btn--ghost btn--sm"
              disabled={refreshMutation.isPending}
            >
              {refreshMutation.isPending ? 'Повтор...' : 'Повторить'}
            </button>
          </div>
        )}

        {/* Status: ready */}
        {status === 'ready' && (
          <>
            {/* Folder selector */}
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--muted)' }}>
                Папка
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => { setSelectedFolderId(undefined); setSelectedChatIds(new Set()) }}
                  className="btn btn--sm"
                  style={{
                    background: selectedFolderId === undefined ? 'var(--teal, #0d9488)' : 'rgba(11,15,20,0.04)',
                    color: selectedFolderId === undefined ? '#fff' : 'var(--ink)',
                    border: '1px solid var(--border)',
                  }}
                >
                  Все диалоги
                </button>
                {folders.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => { setSelectedFolderId(f.id); setSelectedChatIds(new Set()) }}
                    className="btn btn--sm"
                    style={{
                      background: selectedFolderId === f.id ? 'var(--teal, #0d9488)' : 'rgba(11,15,20,0.04)',
                      color: selectedFolderId === f.id ? '#fff' : 'var(--ink)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {f.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Candidates */}
            <div>
              {candidatesQuery.isLoading && (
                <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
                  <Spinner />
                  <span>Загрузка диалогов…</span>
                </div>
              )}

              {candidatesQuery.isSuccess && candidates.length === 0 && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Нет доступных диалогов.
                </p>
              )}

              {candidatesQuery.isSuccess && candidates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
                      Диалоги ({candidates.length})
                    </p>
                    {availableCandidates.length > 0 && (
                      <button
                        onClick={toggleAll}
                        className="text-xs"
                        style={{ color: 'var(--accent)' }}
                      >
                        {allSelected ? 'Снять всё' : 'Выбрать всё'}
                      </button>
                    )}
                  </div>

                  <div
                    className="rounded-lg overflow-hidden"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    {candidates.map((c, idx) => {
                      const isDisabled = c.already_added
                      const isChecked = selectedChatIds.has(c.chat_id)
                      return (
                        <label
                          key={c.chat_id}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                          style={{
                            borderTop: idx > 0 ? '1px solid var(--border)' : undefined,
                            background: isDisabled ? 'rgba(11,15,20,0.02)' : undefined,
                            opacity: isDisabled ? 0.6 : 1,
                            cursor: isDisabled ? 'default' : 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && toggleCandidate(c.chat_id)}
                            className="rounded"
                            style={{ accentColor: 'var(--teal)' }}
                          />
                          <span className="flex-1 min-w-0">
                            <span
                              className="block text-sm truncate"
                              style={{ color: 'var(--ink)' }}
                            >
                              {c.title ?? `ID ${c.chat_id}`}
                            </span>
                            {c.username && (
                              <span className="block text-xs" style={{ color: 'var(--muted)' }}>
                                @{c.username}
                              </span>
                            )}
                          </span>
                          {c.already_added && (
                            <span
                              className="text-xs rounded-full px-2 py-0.5 flex-shrink-0"
                              style={{ background: 'rgba(11,15,20,0.06)', color: 'var(--muted)' }}
                            >
                              уже добавлен
                            </span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {status === 'ready' && (
        <div
          className="px-5 py-4 space-y-2 flex-shrink-0"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          {importResult && (
            <p className="text-xs" style={{ color: 'var(--teal)' }}>{importResult}</p>
          )}
          {applyMutation.isError && (
            <p className="text-xs text-red-500">
              {applyMutation.error instanceof ApiError
                ? applyMutation.error.message
                : 'Ошибка импорта'}
            </p>
          )}
          <button
            onClick={() => applyMutation.mutate()}
            disabled={selectedChatIds.size === 0 || applyMutation.isPending}
            className="btn btn--sm w-full justify-center disabled:opacity-40"
            style={{
              background: 'var(--teal, #0d9488)',
              color: '#fff',
              border: 'none',
            }}
          >
            {applyMutation.isPending
              ? 'Импорт...'
              : `Импортировать выбранные (${selectedChatIds.size})`}
          </button>
        </div>
      )}
    </aside>
  )
}

function ManagedImportDrawer({
  projectId,
  onClose,
}: {
  projectId: number
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined)
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set())
  const [importResult, setImportResult] = useState<string | null>(null)
  const refreshedRef = useRef(false)

  const readersQuery = useQuery({
    queryKey: ['managed-readers', 6477828562],
    queryFn: () => api.getManagedReaders(6477828562),
  })
  const reader: ManagedReader | undefined = readersQuery.data?.readers[0]

  const refreshMutation = useMutation({
    mutationFn: async () => {
      if (!reader) throw new Error('SENSOR userbot не найден')
      await api.grantManagedReader(projectId, reader.profile_id)
      return api.refreshManagedReaderSnapshot(projectId, reader.profile_id)
    },
  })

  useEffect(() => {
    if (reader && !refreshedRef.current) {
      refreshedRef.current = true
      refreshMutation.mutate()
    }
  }, [reader])

  const foldersQuery = useQuery({
    queryKey: ['managed-import-folders', projectId, reader?.profile_id],
    queryFn: () => api.getManagedReaderFolders(projectId, reader!.profile_id),
    enabled: !!reader,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'requested' || status === 'none' || status === undefined) return 2000
      return false
    },
  })

  const status = foldersQuery.data?.status ?? 'none'
  const folders: ImportFolder[] = foldersQuery.data?.folders ?? []

  const candidatesQuery = useQuery({
    queryKey: ['managed-import-candidates', projectId, reader?.profile_id, selectedFolderId],
    queryFn: () => api.getManagedReaderCandidates(projectId, reader!.profile_id, selectedFolderId),
    enabled: !!reader && status === 'ready',
  })

  const candidates: ImportCandidate[] = candidatesQuery.data?.candidates ?? []
  const availableCandidates = candidates.filter((c) => !c.already_added && !c.reader_conflict)
  const availableChatIds = new Set(availableCandidates.map((c) => chatIdKey(c.chat_id)))
  const selectedChatIdNumbers = Array.from(selectedChatIds)
    .map((chatId) => numericChatId(chatId))
    .filter((chatId) => Number.isFinite(chatId))

  useEffect(() => {
    setSelectedChatIds((prev) => {
      const next = new Set<string>()
      prev.forEach((id) => {
        if (availableChatIds.has(id)) next.add(id)
      })
      const unchanged = next.size === prev.size && Array.from(next).every((id) => prev.has(id))
      return unchanged ? prev : next
    })
  }, [candidatesQuery.dataUpdatedAt])

  function toggleCandidate(chatId: unknown) {
    const normalizedChatId = chatIdKey(chatId)
    setSelectedChatIds((prev) => {
      const next = new Set(prev)
      if (next.has(normalizedChatId)) next.delete(normalizedChatId)
      else next.add(normalizedChatId)
      return next
    })
  }

  function toggleAll() {
    const ids = availableCandidates.map((c) => chatIdKey(c.chat_id))
    const allSelected = ids.every((id) => selectedChatIds.has(id))
    setSelectedChatIds(allSelected ? new Set() : new Set(ids))
  }

  const applyMutation = useMutation({
    mutationFn: () => {
      if (!reader) throw new Error('SENSOR userbot не найден')
      return api.applyManagedReaderImport(
        projectId,
        reader.profile_id,
        selectedChatIdNumbers,
      )
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      void queryClient.invalidateQueries({ queryKey: ['managed-import-candidates', projectId] })
      setSelectedChatIds(new Set())
      const clientSkipped = result.skipped_existing_client_reader
      setImportResult(
        `Добавлено: ${result.added}, клиентский userbot сохранён: ${clientSkipped}, сверх лимита: ${result.skipped_by_limit}`,
      )
    },
  })

  function handleRetry() {
    void queryClient.invalidateQueries({ queryKey: ['managed-import-folders', projectId] })
    refreshMutation.mutate()
  }

  const allSelected =
    availableCandidates.length > 0 &&
    availableCandidates.every((c) => selectedChatIds.has(chatIdKey(c.chat_id)))
  const noReader = readersQuery.isSuccess && !reader

  return (
    <aside
      className="fixed inset-y-0 right-0 z-30 flex flex-col shadow-xl"
      style={{
        width: '440px',
        maxWidth: '100vw',
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      <div
        className="flex items-center justify-between px-5 py-4 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          Источники SENSOR
        </span>
        <button onClick={onClose} className="rounded p-1" style={{ color: 'var(--muted)' }}>
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4 text-sm">
        {readersQuery.isLoading && (
          <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
            <Spinner />
            <span>Ищу SENSOR userbot…</span>
          </div>
        )}

        {noReader && (
          <div
            className="rounded-lg p-4 text-sm"
            style={{ background: 'rgba(11,15,20,0.04)', color: 'var(--muted)' }}
          >
            SENSOR userbot 6477828562 не найден или не авторизован.
          </div>
        )}

        {reader && (
          <p className="text-xs" style={{ color: 'var(--muted)' }}>
            Добавляем источники из каталога SENSOR. Клиент сам не сможет выбрать новые SENSOR-источники.
          </p>
        )}

        {reader && (status === 'requested' || status === 'none') && (
          <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
            <Spinner />
            <span>Синхронизирую диалоги SENSOR…</span>
          </div>
        )}

        {reader && status === 'failed' && (
          <div className="space-y-3">
            <p className="text-red-500">Не удалось синхронизировать SENSOR userbot.</p>
            <button
              onClick={handleRetry}
              className="btn btn--ghost btn--sm"
              disabled={refreshMutation.isPending}
            >
              {refreshMutation.isPending ? 'Повтор...' : 'Повторить'}
            </button>
          </div>
        )}

        {reader && status === 'ready' && (
          <>
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--muted)' }}>
                Папка
              </p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => { setSelectedFolderId(undefined); setSelectedChatIds(new Set()) }}
                  className="btn btn--sm"
                  style={{
                    background: selectedFolderId === undefined ? 'var(--teal, #0d9488)' : 'rgba(11,15,20,0.04)',
                    color: selectedFolderId === undefined ? '#fff' : 'var(--ink)',
                    border: '1px solid var(--border)',
                  }}
                >
                  Все диалоги
                </button>
                {folders.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => { setSelectedFolderId(f.id); setSelectedChatIds(new Set()) }}
                    className="btn btn--sm"
                    style={{
                      background: selectedFolderId === f.id ? 'var(--teal, #0d9488)' : 'rgba(11,15,20,0.04)',
                      color: selectedFolderId === f.id ? '#fff' : 'var(--ink)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {f.title}
                  </button>
                ))}
              </div>
            </div>

            <div>
              {candidatesQuery.isLoading && (
                <div className="flex items-center gap-2" style={{ color: 'var(--muted)' }}>
                  <Spinner />
                  <span>Загрузка диалогов…</span>
                </div>
              )}

              {candidatesQuery.isSuccess && candidates.length === 0 && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Нет доступных диалогов.
                </p>
              )}

              {candidatesQuery.isSuccess && candidates.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
                      Диалоги ({candidates.length}) · выбрано {selectedChatIds.size}
                    </p>
                    {availableCandidates.length > 0 && (
                      <button
                        onClick={toggleAll}
                        className="text-xs"
                        style={{ color: 'var(--accent)' }}
                      >
                        {allSelected ? 'Снять всё' : 'Выбрать всё'}
                      </button>
                    )}
                  </div>

                  <div
                    className="rounded-lg overflow-hidden"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    {candidates.map((c, idx) => {
                      const isDisabled = c.already_added || c.reader_conflict
                      const chatId = chatIdKey(c.chat_id)
                      const isChecked = selectedChatIds.has(chatId)
                      return (
                        <label
                          key={chatId}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                          style={{
                            borderTop: idx > 0 ? '1px solid var(--border)' : undefined,
                            background: isDisabled ? 'rgba(11,15,20,0.02)' : undefined,
                            opacity: isDisabled ? 0.6 : 1,
                            cursor: isDisabled ? 'default' : 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && toggleCandidate(chatId)}
                            className="rounded"
                            style={{ accentColor: 'var(--teal)' }}
                          />
                          <span className="flex-1 min-w-0">
                            <span
                              className="block text-sm truncate"
                              style={{ color: 'var(--ink)' }}
                            >
                              {c.title ?? `ID ${chatId}`}
                            </span>
                            {c.username && (
                              <span className="block text-xs" style={{ color: 'var(--muted)' }}>
                                @{c.username}
                              </span>
                            )}
                          </span>
                          {isDisabled && (
                            <span
                              className="text-xs rounded-full px-2 py-0.5 flex-shrink-0"
                              style={{ background: 'rgba(11,15,20,0.06)', color: 'var(--muted)' }}
                            >
                              {c.reader_conflict ? 'читает клиент' : 'уже добавлен'}
                            </span>
                          )}
                        </label>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {reader && status === 'ready' && (
        <div
          className="px-5 py-4 space-y-2 flex-shrink-0"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          {importResult && (
            <p className="text-xs" style={{ color: 'var(--teal)' }}>{importResult}</p>
          )}
          {applyMutation.isError && (
            <p className="text-xs text-red-500">
              {applyMutation.error instanceof ApiError
                ? applyMutation.error.message
                : 'Ошибка импорта'}
            </p>
          )}
          <button
            onClick={() => applyMutation.mutate()}
            disabled={selectedChatIdNumbers.length === 0 || applyMutation.isPending}
            className="btn btn--sm w-full justify-center disabled:opacity-40"
            style={{
              background: 'var(--teal, #0d9488)',
              color: '#fff',
              border: 'none',
            }}
          >
            {applyMutation.isPending
              ? 'Импорт...'
              : `Добавить SENSOR-источники (${selectedChatIds.size})`}
          </button>
        </div>
      )}
    </aside>
  )
}

// ---------------------------------------------------------------------------
// Org-wide view (no project selected)
// ---------------------------------------------------------------------------

function OrgSourceRow({
  source,
  showUserbot,
  projectNumbers,
  onNotify,
}: {
  source: OrgSource
  showUserbot: boolean
  projectNumbers: Map<number, number>
  onNotify: (text: string, kind: 'ok' | 'err') => void
}) {
  const queryClient = useQueryClient()
  const sourceUrl = telegramChatUrl(source)
  const title = source.title ?? `ID ${source.chat_id}`
  const username = source.username ? `@${source.username.replace(/^@/, '')}` : null
  const yieldPct = scorePercent(source.source_yield_score)
  const noisePct = scorePercent(source.source_noise_score)
  const firstProjectId = source.projects[0]?.id
  const leadsHref = firstProjectId
    ? `/leads?project=${firstProjectId}${source.username ? `&source=${encodeURIComponent(source.username)}` : ''}`
    : '/leads'
  const qualityTitle = [source.quality_reason, source.recommendation]
    .filter(Boolean)
    .join(' ')

  const [pendingRemoveId, setPendingRemoveId] = useState<number | null>(null)

  const removeMutation = useMutation({
    mutationFn: (vars: { projectId: number; sourceStateId: number; projectName: string }) =>
      api.removeSource(vars.projectId, vars.sourceStateId),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      onNotify(`Источник убран из проекта «${vars.projectName}»`, 'ok')
      setPendingRemoveId(null)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Не удалось убрать источник'
      onNotify(msg, 'err')
      setPendingRemoveId(null)
    },
  })

  function confirmRemove(p: OrgSource['projects'][number]) {
    if (p.source_state_id == null) return
    const label = projectLabel(p, projectNumbers)
    removeMutation.mutate({
      projectId: p.id,
      sourceStateId: p.source_state_id,
      projectName: label,
    })
  }

  return (
    <tr>
      <td>
        <div className="src-name">
          <span className="avatar">{sourceInitials(source.title, source.chat_id)}</span>
          <span className="meta">
            {sourceUrl ? (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer">
                <b>{title}</b>
              </a>
            ) : (
              <b>{title}</b>
            )}
            <small>{username ?? `chat ${source.chat_id}`}</small>
          </span>
        </div>
      </td>
      <td>
        <div className="source-chips">
          {source.projects.length === 0 ? (
            <span className="text-xs" style={{ color: 'var(--muted)' }}>—</span>
          ) : (
            source.projects.map((p) => (
              <span key={p.id} className="source-chip-wrap">
                <a
                  href={`/projects?project=${p.id}`}
                  className="source-chip"
                >
                  {projectLabel(p, projectNumbers)}
                </a>
                {p.source_state_id != null && p.source_management_mode !== 'sensor_managed' && (
                  <span className="source-chip__remove-wrap">
                    <button
                      type="button"
                      className="source-chip__remove"
                      title={`Убрать из проекта «${projectLabel(p, projectNumbers)}»`}
                      aria-label="Убрать источник из проекта"
                      disabled={removeMutation.isPending}
                      onClick={() => setPendingRemoveId(pendingRemoveId === p.id ? null : p.id)}
                    >
                      −
                    </button>
                    {pendingRemoveId === p.id && (
                      <span className="chip-confirm-pop" role="dialog">
                        <span className="chip-confirm-pop__text">Убрать?</span>
                        <button
                          type="button"
                          className="chip-confirm-pop__yes"
                          disabled={removeMutation.isPending}
                          onClick={() => confirmRemove(p)}
                        >
                          {removeMutation.isPending ? '…' : 'Да'}
                        </button>
                        <button
                          type="button"
                          className="chip-confirm-pop__no"
                          onClick={() => setPendingRemoveId(null)}
                        >
                          Нет
                        </button>
                      </span>
                    )}
                  </span>
                )}
              </span>
            ))
          )}
        </div>
      </td>
      <td>
        <div className="source-chips">
          {(source.acquisition_methods.length ? source.acquisition_methods : ['manual_link']).map((method) => (
            <span
              key={method}
              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap"
              style={sourceAcquisitionStyle(method)}
            >
              {sourceAcquisitionText(method)}
            </span>
          ))}
        </div>
      </td>
      {showUserbot && (
        <td>
          <div className="source-chips">
            {source.userbot_labels.length === 0 ? (
              <span className="text-xs" style={{ color: 'var(--muted)' }}>—</span>
            ) : (
              source.userbot_labels.map((label) => (
                <span
                  key={label}
                  className="source-chip source-chip--muted"
                >
                  {label}
                </span>
              ))
            )}
          </div>
        </td>
      )}
      <td className="text-right">
        <div className="l-cell">
          <span className="l-count">{source.total_leads}</span>
          <span className="l-count__sub">
            <span>{source.live_leads_count} live</span>
            <span>{source.history_leads_count} ист.</span>
          </span>
        </div>
      </td>
      <td>
        <AccessBadge status={source.access_status} />
        {source.access_reason && source.access_status !== 'ok' && (
          <span className="source-access-reason">{source.access_reason}</span>
        )}
      </td>
      <td>
        <div className="fb-pills">
          <span className="fb-pill fb-pill--up" title="Понравилось">
            +{source.liked_leads_count}
          </span>
          <span className="fb-pill fb-pill--down" title="Не подошло">
            −{source.disliked_leads_count}
          </span>
          <span className="fb-pill fb-pill--unsure" title="Не уверен">
            ?{source.unsure_leads_count}
          </span>
        </div>
      </td>
      <td>
        <div className="source-score" title={`Сигнал ${yieldPct}%, шум ${noisePct}%`}>
          <div className="source-score__metric">
            <span className="source-score__label">Сигнал</span>
            <span className="source-score__track">
              <span
                className="source-score__fill source-score__fill--signal"
                style={{ width: `${yieldPct}%` }}
              />
            </span>
            <span className="source-score__val">
              {(source.source_yield_score ?? 0).toFixed(2)}
            </span>
          </div>
          <div className="source-score__metric">
            <span className="source-score__label">Шум</span>
            <span className="source-score__track">
              <span
                className="source-score__fill source-score__fill--noise"
                style={{ width: `${noisePct}%` }}
              />
            </span>
            <span className="source-score__val">
              {(source.source_noise_score ?? 0).toFixed(2)}
            </span>
          </div>
        </div>
      </td>
      <td>
        <span
          className={`source-quality source-quality--${source.quality_label}`}
          title={qualityTitle || undefined}
        >
          {sourceQualityText(source.quality_label)}
        </span>
      </td>
      <td className="source-date">
        {formatDate(source.last_lead_at)}
      </td>
      <td>
        <div className="row-actions">
          <a href={leadsHref} title="Смотреть лиды" aria-label="Смотреть лиды">
            ≡
          </a>
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Открыть Telegram"
              aria-label="Открыть Telegram"
            >
              ↗
            </a>
          )}
        </div>
      </td>
    </tr>
  )
}

function SourcesProjectMultiSelect({
  projects,
  selectedIds,
  onChange,
}: {
  projects: ProjectResponse[] | undefined
  selectedIds: number[]
  onChange: (ids: number[]) => void
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const projectNumbers = useMemo(() => buildProjectNumberMap(projects), [projects])
  const selectedNames = useMemo(
    () => selectedIds.map((id) => projectLabelById(id, projects, projectNumbers)),
    [projectNumbers, projects, selectedIds],
  )
  const label =
    selectedIds.length === 0
      ? 'Все проекты'
      : selectedIds.length === 1
        ? selectedNames[0]
        : `Выбрано: ${selectedIds.length}`

  const toggle = (id: number) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((item) => item !== id) : [...selectedIds, id])
  }

  return (
    <div className="project-filter sources-project-filter" ref={rootRef}>
      <button
        key={selectedIds.join(',') || 'all'}
        type="button"
        className={`project-filter__button${open ? ' is-open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{label}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="project-filter__menu" role="listbox" aria-multiselectable="true">
          <button
            type="button"
            className={`project-filter__option${selectedIds.length === 0 ? ' is-selected' : ''}`}
            onClick={() => onChange([])}
          >
            <span className="project-filter__check">
              {selectedIds.length === 0 && (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                  <path d="M5 12l4 4 10-10" />
                </svg>
              )}
            </span>
            Все проекты
          </button>
          {(projects ?? []).map((project) => {
            const selected = selectedIds.includes(project.id)
            return (
              <button
                key={project.id}
                type="button"
                className={`project-filter__option${selected ? ' is-selected' : ''}`}
                onClick={() => toggle(project.id)}
              >
                <span className="project-filter__check">
                  {selected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
                      <path d="M5 12l4 4 10-10" />
                    </svg>
                  )}
                </span>
                <span>{projectLabel(project, projectNumbers)}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function OrgSourcesView() {
  const [searchParams, setSearchParams] = useSearchParams()
  const sourceProjectsParam = searchParams.get('sourceProjects') ?? searchParams.get('sourceProject')
  const parseProjectIds = (raw: string | null): number[] =>
    (raw ?? '')
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isFinite(id) && id > 0)
  const [selectedProjectIds, setSelectedProjectIds] = useState<number[]>(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem('sensor.sources.projectFilter') : null
    return parseProjectIds(sourceProjectsParam ?? stored)
  })
  const [sortBy, setSortBy] = useState<OrgSourceSort>('leads_desc')
  const [qualityFilter, setQualityFilter] = useState<OrgSourceFilter>('all')
  const [search, setSearch] = useState('')
  const [minLeads, setMinLeads] = useState(0)
  useEffect(() => {
    if (sourceProjectsParam !== null) {
      const parsed = parseProjectIds(sourceProjectsParam)
      setSelectedProjectIds(parsed)
      try {
        if (parsed.length) {
          window.localStorage.setItem('sensor.sources.projectFilter', parsed.join(','))
        } else {
          window.localStorage.removeItem('sensor.sources.projectFilter')
        }
      } catch {
        /* storage unavailable */
      }
    }
  }, [sourceProjectsParam])
  const serverProjectId = selectedProjectIds.length === 1 ? selectedProjectIds[0] : undefined
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['sources', 'all', serverProjectId ?? 'all'],
    queryFn: () => api.getAllSources(serverProjectId),
    placeholderData: (prev) => prev,
  })
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects(),
  })
  const projectNumbers = useMemo(() => buildProjectNumberMap(projects), [projects])
  // Drop stale project ids that no longer exist in the org (e.g. after a
  // project was deleted or the localStorage filter was left over from a
  // previous org). This keeps the multi-select counter honest and unblocks
  // the single-project catalog path.
  useEffect(() => {
    if (!projects) return
    const validIds = new Set(projects.map((p) => p.id))
    setSelectedProjectIds((prev) => {
      const next = prev.filter((id) => validIds.has(id))
      if (next.length === prev.length) return prev
      try {
        if (next.length === 0) {
          window.localStorage.removeItem('sensor.sources.projectFilter')
        } else {
          window.localStorage.setItem('sensor.sources.projectFilter', next.join(','))
        }
      } catch {
        /* storage unavailable */
      }
      return next
    })
  }, [projects])
  // Similar-sources is per-project. Use the filtered project, else default to
  // the first project so the action is never a dead/disabled button.
  const catalogProjectId =
    selectedProjectIds.length === 1 ? selectedProjectIds[0] : selectedProjectIds.length === 0 ? projects?.[0]?.id ?? null : null
  const singleSelectedProjectId = selectedProjectIds.length === 1 ? selectedProjectIds[0] : null
  const { data: userbots } = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
  })
  const { user } = useAuth()
  const aiAvailable = !!user && user.has_ai
  const hasSourceReaderLabels = (data?.sources ?? []).some((source) => source.userbot_labels.length > 0)
  const showUserbot = (userbots?.userbots.length ?? 0) > 1 || hasSourceReaderLabels

  // Single project is scoped by the server; multi-project selection is narrowed
  // client-side because the endpoint currently accepts only one project_id.
  const projectSources = useMemo(() => {
    const items = data?.sources ?? []
    if (selectedProjectIds.length <= 1) return items
    const selected = new Set(selectedProjectIds)
    return items.filter((source) => source.projects.some((project) => selected.has(project.id)))
  }, [data, selectedProjectIds])

  const filterCounts = useMemo(() => {
    const counts = Object.fromEntries(
      ORG_SOURCE_FILTERS.map((f) => [f.key, 0]),
    ) as Record<OrgSourceFilter, number>
    for (const source of projectSources) {
      counts.all += 1
      const bucket = sourceQualityBucket(source)
      // `new` is no longer a chip, so guard against incrementing a missing key.
      if (bucket in counts) counts[bucket] += 1
    }
    return counts
  }, [projectSources])

  // Upper bound for the "Минимум лидов" slider — the busiest source in view.
  const maxLeads = useMemo(
    () => projectSources.reduce((m, s) => Math.max(m, s.total_leads), 0),
    [projectSources],
  )

  const visibleSources = useMemo(() => {
    const items = [...projectSources]
    const normalizedSearch = search.trim().toLowerCase()
    const filtered = items.filter((source) => {
      if (source.total_leads < minLeads) return false
      if (qualityFilter !== 'all' && sourceQualityBucket(source) !== qualityFilter) {
        return false
      }
      if (!normalizedSearch) return true
      const haystack = [
        source.title,
        source.username,
        source.source_url,
        String(source.chat_id),
        ...source.projects.map((p) => p.name ?? ''),
      ]
        .join(' ')
        .toLowerCase()
      return haystack.includes(normalizedSearch)
    })

    filtered.sort((a, b) => {
      if (sortBy === 'leads_asc') return a.total_leads - b.total_leads
      if (sortBy === 'leads_desc') return b.total_leads - a.total_leads
      if (sortBy === 'yield_desc') {
        return (b.source_yield_score ?? 0) - (a.source_yield_score ?? 0)
      }
      if (sortBy === 'noise_desc') {
        return (b.source_noise_score ?? 0) - (a.source_noise_score ?? 0)
      }
      const at = a.last_lead_at ? new Date(a.last_lead_at).getTime() : 0
      const bt = b.last_lead_at ? new Date(b.last_lead_at).getTime() : 0
      return sortBy === 'last_asc' ? at - bt : bt - at
    })

    return filtered
  }, [projectSources, qualityFilter, search, sortBy, minLeads])

  const activeSources = projectSources.filter((s) => s.access_status === 'ok').length
  const catalogSeedSourceIds = useMemo(() => {
    if (!catalogProjectId || selectedProjectIds.length !== 1) return []
    const ids: number[] = []
    const seen = new Set<number>()
    for (const source of visibleSources) {
      const project = source.projects.find((item) => item.id === catalogProjectId)
      const id = project?.source_state_id
      if (id && !seen.has(id)) {
        seen.add(id)
        ids.push(id)
      }
    }
    return ids
  }, [catalogProjectId, selectedProjectIds.length, visibleSources])
  const catalogHref = catalogProjectId
    ? `/sources?project=${catalogProjectId}&tab=recommendations${
        catalogSeedSourceIds.length ? `&seedSources=${catalogSeedSourceIds.join(',')}` : ''
      }`
    : ''
  const catalogDisabledByEmptyFilter = selectedProjectIds.length === 1 && visibleSources.length === 0

  // Resizable columns ------------------------------------------------------
  // Column order here must match the <td> order rendered by OrgSourceRow.
  const columns = useMemo(() => {
    const cols: Array<{
      key: string
      label: string
      align?: 'right'
      resizable?: boolean
      defaultW: number
    }> = [
      { key: 'source', label: 'Источник', defaultW: 260 },
      { key: 'projects', label: 'Проекты', defaultW: 240 },
      { key: 'acquisition', label: 'Добавлен', defaultW: 132 },
    ]
    if (showUserbot) cols.push({ key: 'userbot', label: 'Телеграм доступ', defaultW: 150 })
    cols.push(
      { key: 'leads', label: 'Лидов', align: 'right', defaultW: 116 },
      { key: 'access', label: 'Доступ', defaultW: 116 },
      { key: 'feedback', label: 'Фидбек', defaultW: 150 },
      { key: 'score', label: 'Сигнал / шум', defaultW: 156 },
      { key: 'quality', label: 'Рекомендация', defaultW: 178 },
      { key: 'last', label: 'Посл. лид', defaultW: 124 },
      { key: 'actions', label: '', resizable: false, defaultW: 92 },
    )
    return cols
  }, [showUserbot])

  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      return JSON.parse(localStorage.getItem('sensor.sources.colw') ?? '{}')
    } catch {
      return {}
    }
  })

  const dragRef = useRef<{ key: string; startX: number; startW: number } | null>(null)
  function onResizeStart(e: React.MouseEvent, key: string, currentW: number) {
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = { key, startX: e.clientX, startW: currentW }
    document.body.style.cursor = 'col-resize'
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const next = Math.max(72, d.startW + (ev.clientX - d.startX))
      setColWidths((w) => ({ ...w, [d.key]: next }))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      dragRef.current = null
      document.body.style.cursor = ''
      setColWidths((w) => {
        try {
          localStorage.setItem('sensor.sources.colw', JSON.stringify(w))
        } catch {
          /* storage unavailable — keep widths in memory only */
        }
        return w
      })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const widthFor = (key: string, fallback: number) => colWidths[key] ?? fallback
  const totalWidth = columns.reduce((sum, c) => sum + widthFor(c.key, c.defaultW), 0)

  // Transient toast for source-removal feedback ----------------------------
  const [toast, setToast] = useState<{ text: string; kind: 'ok' | 'err' } | null>(null)
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setProjectFilterAndUrl = (ids: number[]) => {
    setSelectedProjectIds(ids)
    const serialized = ids.join(',')
    try {
      if (!serialized) {
        window.localStorage.removeItem('sensor.sources.projectFilter')
      } else {
        window.localStorage.setItem('sensor.sources.projectFilter', serialized)
      }
    } catch {
      /* storage unavailable */
    }
    setQualityFilter('all')
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('sourceProject')
    if (!serialized) {
      nextParams.delete('sourceProjects')
    } else {
      nextParams.set('sourceProjects', serialized)
    }
    setSearchParams(nextParams, { replace: true })
  }
  const notify = (text: string, kind: 'ok' | 'err') => {
    setToast({ text, kind })
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 4000)
  }
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
  }, [])

  return (
    <div className="p-8 sources-view">
      <div className="sources-head">
        <div className="sources-head__text">
          <h1>Источники</h1>
          <p className="sources-head__copy">
            Чаты и каналы, в которых SENSOR ищет спрос. Качество источника считается по лидам, отзывам и доступу.
          </p>
          {data && (
            <p className="sources-head__meta">
              {projectSources.length} источников · {activeSources} активных
            </p>
          )}
        </div>
      </div>

      {data && (data.sources.length > 0 || (projects?.length ?? 0) > 0) && (
        <div className="sources-toolbar">
          <label className="sources-search">
            <span>⌕</span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названию, @username, проектам..."
            />
          </label>
          <div className="sources-filters" role="tablist" aria-label="Фильтр качества источников">
            {ORG_SOURCE_FILTERS.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={qualityFilter === filter.key ? 'is-active' : ''}
                onClick={() => setQualityFilter(filter.key)}
              >
                {filter.label}
                <span>{filterCounts[filter.key]}</span>
              </button>
            ))}
          </div>
          {maxLeads > 0 && (
            <label className="leads-slider" title="Показывать источники минимум с этим числом лидов">
              <span className="leads-slider__label">Минимум лидов</span>
              <input
                type="range"
                min={0}
                max={maxLeads}
                step={1}
                value={Math.min(minLeads, maxLeads)}
                onChange={(e) => setMinLeads(Number(e.target.value))}
                style={{
                  // teal fill up to the thumb, neutral track after — matches landing
                  background: `linear-gradient(to right, var(--teal) 0%, var(--teal) ${
                    maxLeads ? (Math.min(minLeads, maxLeads) / maxLeads) * 100 : 0
                  }%, var(--surface-2) ${
                    maxLeads ? (Math.min(minLeads, maxLeads) / maxLeads) * 100 : 0
                  }%, var(--surface-2) 100%)`,
                }}
              />
              <span
                key={`${minLeads}/${maxLeads}`}
                className="leads-slider__value"
                title="Выбранный минимум / максимум лидов среди источников"
              >
                {minLeads} / {maxLeads}
              </span>
            </label>
          )}
          <div className="sources-selects">
            <SourcesProjectMultiSelect
              projects={projects}
              selectedIds={selectedProjectIds}
              onChange={setProjectFilterAndUrl}
            />
            <Select
              key={`sources-sort-${sortBy}`}
              style={{ minWidth: 170, maxWidth: 240 }}
              align="right"
              ariaLabel="Сортировка источников"
              value={sortBy}
              onChange={(v) => setSortBy(v as OrgSourceSort)}
              options={[
                { value: 'leads_desc', label: 'Сортировка: лидов ↓' },
                { value: 'leads_asc', label: 'Сортировка: лидов ↑' },
                { value: 'last_desc', label: 'Последний лид: новые' },
                { value: 'last_asc', label: 'Последний лид: старые' },
                { value: 'yield_desc', label: 'Сигнал ↓' },
                { value: 'noise_desc', label: 'Шум ↓' },
              ]}
            />
          </div>
          <div className="sources-toolbar__actions">
            {catalogProjectId && !catalogDisabledByEmptyFilter ? (
              <a
                href={catalogHref}
                className="btn btn--ai btn--sm"
                title={
                  catalogSeedSourceIds.length
                    ? `Подобрать похожие источники по текущим ${catalogSeedSourceIds.length} источникам в таблице`
                    : 'Подобрать похожие источники для выбранного проекта'
                }
              >
                Подбор похожих источников
              </a>
            ) : (
              <button
                type="button"
                className="btn btn--ai btn--sm"
                disabled
                title={
                  catalogDisabledByEmptyFilter
                    ? 'Текущий фильтр не оставил источников для подбора'
                    : 'Выберите один проект в фильтре, чтобы открыть подбор похожих источников'
                }
              >
                Подбор похожих источников
              </button>
            )}
            {selectedProjectIds.length === 1 && (
              <a href={`/sources?project=${selectedProjectIds[0]}`} className="btn btn--sm sources-add-link">
                Добавить источник +
              </a>
            )}
            {singleSelectedProjectId !== null && aiAvailable && (
              <HistoryScanControl projectId={singleSelectedProjectId} aiAvailable={aiAvailable} />
            )}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
          <div
            className="h-4 w-4 animate-spin rounded-full"
            style={{
              border: '2px solid var(--teal)',
              borderTopColor: 'transparent',
            }}
          />
          Загрузка...
        </div>
      )}

      {isError && (
        <div
          className="rounded-xl px-6 py-5 text-sm"
          style={{
            border: '1px solid var(--danger-line, #f1b8b8)',
            background: 'var(--danger-soft, #fff4f4)',
            color: 'var(--danger-ink, #c0392b)',
            lineHeight: 1.5,
          }}
        >
          Не удалось загрузить общий список источников.
          <br />
          {error instanceof ApiError ? error.message : 'Откройте источники через конкретный проект.'}
        </div>
      )}

      {data && visibleSources.length === 0 && (
        <div
          className="rounded-xl px-6 py-10 text-center text-sm"
          style={{
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: 'var(--muted)',
          }}
        >
          {selectedProjectIds.length === 0
            ? 'Общий список пока пуст. Выберите проект выше, чтобы открыть его источники или подобрать похожие.'
            : 'В выбранном проекте источники не найдены'}
        </div>
      )}

      {data && visibleSources.length > 0 && (
        <div className="sources-shell">
          <table
            className="sources-table sources-table--org"
            style={{ tableLayout: 'fixed', width: totalWidth }}
          >
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={{ width: widthFor(c.key, c.defaultW) }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={c.align === 'right' ? 'text-right' : undefined}>
                    {c.label}
                    {c.resizable !== false && (
                      <span
                        className="col-resizer"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Изменить ширину «${c.label}»`}
                        onMouseDown={(e) => onResizeStart(e, c.key, widthFor(c.key, c.defaultW))}
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleSources.map((s) => (
                <OrgSourceRow
                  key={s.chat_id}
                  source={s}
                  showUserbot={showUserbot}
                  projectNumbers={projectNumbers}
                  onNotify={notify}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div
          className={`sources-toast sources-toast--${toast.kind}`}
          role="status"
          aria-live="polite"
        >
          {toast.text}
        </div>
      )}
    </div>
  )
}

function RecommendationRow({
  item,
  onAdd,
  isAdding,
}: {
  item: SourceRecommendation
  onAdd: () => void
  isAdding: boolean
}) {
  const kindLabel: Record<SourceRecommendation['kind'], string> = {
    channel: 'Канал',
    megagroup: 'Группа',
    chat: 'Чат',
  }
  const relationLabel = (() => {
    if (item.edge_type === 'audience_overlap') {
      return item.common_count != null
        ? `пересечение аудиторий: ${item.common_count} общих авторов${
            item.overlap_ratio != null ? `, ${Math.round(item.overlap_ratio * 100)}%` : ''
          }`
        : 'пересечение аудиторий'
    }
    if (item.edge_type === 'semantic_match') return 'похожая тематика'
    if (item.edge_type === 'linked_source') return 'связан в описании источника'
    if (item.edge_type === 'geo_match') return 'географическое совпадение'
    if (item.edge_type === 'text_profile_match') return 'похожий профиль источника'
    if (item.edge_type === 'external_catalog_match') return 'внешний каталог'
    return item.rank != null ? `рекомендация Telegram #${item.rank}` : 'рекомендация Telegram'
  })()
  const actionDisabled = item.already_added || !item.username || isAdding
  const actionTitle = item.already_added
    ? 'Источник уже есть в проекте'
    : !item.username
      ? 'Можно добавить только публичный @username'
      : 'Добавить в проект'
  return (
    <tr>
      <td className="px-4 py-3">
        {item.source_url ? (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:underline"
            style={{ color: 'var(--ink)' }}
          >
            {item.title}
          </a>
        ) : (
          <span className="font-medium" style={{ color: 'var(--ink)' }}>
            {item.title}
          </span>
        )}
        <div className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
          {item.username ? `@${item.username}` : `chat ${item.chat_id ?? '—'}`}
        </div>
      </td>
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--ink-2)' }}>
        {kindLabel[item.kind]}
        {item.member_count != null && (
          <span className="block text-xs" style={{ color: 'var(--muted)' }}>
            {item.member_count.toLocaleString('ru-RU')} участников
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--ink-2)' }}>
        {item.similar_to_title}
        <span className="block text-xs" style={{ color: 'var(--muted)' }}>
          {relationLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          className="btn btn--ghost btn--sm disabled:opacity-40"
          disabled={actionDisabled}
          onClick={onAdd}
          title={actionTitle}
        >
          {item.already_added ? 'Уже в проекте' : isAdding ? '...' : 'Добавить'}
        </button>
      </td>
    </tr>
  )
}

function SourceRecommendationsPanel({
  projectId,
  locked,
  sourceStateIds,
}: {
  projectId: number
  locked: boolean
  sourceStateIds: number[]
}) {
  const queryClient = useQueryClient()
  const [message, setMessage] = useState<SourceRecommendationsMessage | null>(null)
  const sourceStateKey = sourceStateIds.join(',')
  const { data, isLoading, isError } = useQuery({
    queryKey: ['source-recommendations', projectId, sourceStateKey],
    queryFn: () => api.getSourceRecommendations(projectId, sourceStateIds),
    placeholderData: (prev) => prev,
    enabled: !locked,
  })
  // The web API process owns no userbot; refresh/add run cross-process in the
  // bot process. Enqueue via POST, then poll the job status here.
  const { data: job } = useQuery({
    queryKey: ['source-discovery-job', projectId],
    queryFn: () => api.getSourceDiscoveryJob(projectId),
    enabled: !locked,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'running' || s === 'requested' ? 2500 : false
    },
  })
  const isRunning = job?.status === 'running' || job?.status === 'requested'
  const refreshMutation = useMutation({
    mutationFn: () =>
      api.refreshSourceRecommendations(projectId, sourceStateIds, sourceStateIds.length > 0),
    onSuccess: (j) => {
      queryClient.setQueryData(['source-discovery-job', projectId], j)
      setMessage(null)
    },
    onError: (err) => {
      const raw = err instanceof ApiError ? err.message : ''
      const text = /userbot unavailable|no userbot|has no userbot/i.test(raw)
        ? 'Подбор пока недоступен: подключённый аккаунт не на связи. Попробуйте позже.'
        : raw || 'Не удалось запустить подбор'
      setMessage({ text, error: true })
    },
  })
  const addMutation = useMutation({
    mutationFn: (catalogId: number) => api.addRecommendedSource(projectId, catalogId),
    onSuccess: (j) => {
      queryClient.setQueryData(['source-discovery-job', projectId], j)
      setMessage(null)
    },
    onError: (err) => {
      setMessage({
        text: err instanceof ApiError ? err.message : 'Не удалось добавить источник',
        error: true,
      })
    },
  })
  // React to cross-process job completion: refresh lists + show the outcome.
  const prevJobStatus = useRef<string | undefined>(undefined)
  useEffect(() => {
    const s = job?.status
    const prev = prevJobStatus.current
    if ((prev === 'running' || prev === 'requested') && (s === 'done' || s === 'failed')) {
      void queryClient.invalidateQueries({ queryKey: ['source-recommendations', projectId] })
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      if (s === 'failed') {
        setMessage({ text: job?.error || 'Не удалось выполнить', error: true })
      } else {
        setMessage(formatSourceDiscoveryJobMessage(job))
      }
    }
    prevJobStatus.current = s
  }, [job?.status, job?.kind, job?.result, job?.error, projectId, queryClient])

  if (locked) {
    return (
      <UpsellNotice
        title="Похожие источники — на тарифе Pro"
        description="Находите каналы и чаты, похожие на ваши источники: рекомендации Telegram и пересечение аудиторий. Подключите Pro, чтобы строить карту источников."
        tier="Pro"
      />
    )
  }

  const items = data?.items ?? []
  const isRefreshing = isRunning || refreshMutation.isPending || addMutation.isPending
  const hasItems = items.length > 0
  const visibleMessage = message ?? (!isRefreshing ? formatSourceDiscoveryJobMessage(job) : null)
  const scopeText = sourceStateIds.length
    ? `Подбор ограничен текущими ${sourceStateIds.length} источниками из фильтра`
    : 'Подбор идет по всем источникам проекта'

  return (
    <div
      className="overflow-hidden"
      style={{
        borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
      }}
    >
      {hasItems && (
        <div
          className="flex items-center justify-between gap-4 px-4 py-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <div>
            <p className="text-base font-semibold" style={{ color: 'var(--ink)' }}>
              Похожие источники
            </p>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {data?.total ?? items.length} найдено · рекомендации Telegram и пересечения аудиторий
            </p>
            <p className="text-xs" style={{ color: 'var(--teal)', marginTop: 4 }}>
              {scopeText}
            </p>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm disabled:opacity-40"
            disabled={isRefreshing}
            onClick={() => refreshMutation.mutate()}
          >
            {isRefreshing ? 'Обновляем…' : 'Обновить'}
          </button>
        </div>
      )}

      {visibleMessage && (
        <div
          className="px-4 py-3 text-sm"
          style={{
            color: visibleMessage.error ? 'var(--danger-ink, #c0392b)' : 'var(--teal)',
            borderBottom: hasItems ? '1px solid var(--border)' : undefined,
          }}
        >
          {visibleMessage.text}
        </div>
      )}

      {isLoading && (
        <div className="px-6 py-16 text-center text-base" style={{ color: 'var(--muted)' }}>
          Загрузка…
        </div>
      )}

      {isError && (
        <div className="px-6 py-16 text-center text-base" style={{ color: 'var(--danger-ink, #c0392b)' }}>
          Не удалось загрузить рекомендации
        </div>
      )}

      {!isLoading && !isError && !hasItems && (
        <div className="flex flex-col items-center px-6 py-16 text-center">
          <div
            className="mb-5 flex items-center justify-center"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'color-mix(in oklch, var(--teal) 14%, transparent)',
              color: 'var(--teal)',
            }}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="6" cy="6.5" r="2.3" />
              <circle cx="18" cy="7.5" r="2.3" />
              <circle cx="12" cy="17.5" r="2.3" />
              <path d="M8.1 7.2 15.7 6.8M7.4 8.4 10.9 15.4M16.7 9.4 13.1 15.6" />
            </svg>
          </div>

          <h3 className="text-xl font-semibold" style={{ color: 'var(--ink)' }}>
            Найдём каналы, похожие на ваши источники
          </h3>
          <p
            className="mt-2.5 text-base"
            style={{ color: 'var(--muted)', lineHeight: 1.6, maxWidth: '46ch' }}
          >
            Telegram подскажет каналы с близкой аудиторией, а мы покажем пересечения по вашим
            источникам. Для многих чатов список будет пустым — это нормально.
          </p>
          <p className="mt-3 text-sm" style={{ color: 'var(--teal)' }}>
            {scopeText}
          </p>

          {isRefreshing ? (
            <div
              className="mt-7 flex items-center gap-2.5 text-base"
              style={{ color: 'var(--ink-2)' }}
            >
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full"
                style={{ border: '2px solid var(--teal)', borderTopColor: 'transparent' }}
              />
              <span>Запрашиваем у Telegram… обычно меньше минуты</span>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn--ai mt-7"
              onClick={() => refreshMutation.mutate()}
            >
              Найти похожие источники
            </button>
          )}
        </div>
      )}

      {hasItems && (
        <>
          {isRefreshing && (
            <div
              className="flex items-center gap-2.5 px-4 py-3 text-sm"
              style={{
                color: 'var(--ink-2)',
                borderBottom: '1px solid var(--border)',
                background: 'var(--surface-2)',
              }}
            >
              <span
                className="inline-block h-4 w-4 animate-spin rounded-full"
                style={{ border: '2px solid var(--teal)', borderTopColor: 'transparent' }}
              />
              <span>Обновляем список похожих источников…</span>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead
                className="text-xs font-medium uppercase tracking-wide"
                style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
              >
                <tr>
                  <th className="px-4 py-3 text-left">Источник</th>
                  <th className="px-4 py-3 text-left">Тип</th>
                  <th className="px-4 py-3 text-left">Похож на</th>
                  <th className="px-4 py-3 text-right">Действие</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <RecommendationRow
                    key={item.id}
                    item={item}
                    isAdding={addMutation.isPending && addMutation.variables === item.id}
                    onAdd={() => addMutation.mutate(item.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SourcesPage() {
  const [searchParams] = useSearchParams()
  const projectId = Number(searchParams.get('project')) || undefined
  const seedSourceStateIds = useMemo(
    () => parseSourceStateIdsParam(searchParams.get('seedSources')),
    [searchParams],
  )
  const tabParam = searchParams.get('tab')
  const { data: projectInfo } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.getProject(projectId as number),
    enabled: !!projectId,
  })
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.getProjects(),
    enabled: !!projectId,
  })
  const projectNumbers = useMemo(() => buildProjectNumberMap(projects), [projects])
  const sourcesManaged = projectInfo?.source_management_mode === 'sensor_managed'
  const currentProjectLabel = projectInfo
    ? projectLabel(projectInfo, projectNumbers)
    : projectLabelById(projectId ?? 0, projects, projectNumbers)
  const [selected, setSelected] = useState<SourceStateResponse | null>(null)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  // Toolbar state
  const [showImportDrawer, setShowImportDrawer] = useState(false)
  const [showManagedImportDrawer, setShowManagedImportDrawer] = useState(false)
  const [manualInput, setManualInput] = useState('')
  const [manualMsg, setManualMsg] = useState<{ text: string; error: boolean } | null>(null)
  const [recheckMsg, setRecheckMsg] = useState<string | null>(null)
  const [connectUserbotOpen, setConnectUserbotOpen] = useState(false)
  const [showManagedAddNotice, setShowManagedAddNotice] = useState(false)
  const [activeTab, setActiveTab] = useState<ProjectSourcesTab>(
    tabParam === 'recommendations' ? 'recommendations' : 'sources',
  )
  useEffect(() => {
    setActiveTab(tabParam === 'recommendations' ? 'recommendations' : 'sources')
  }, [tabParam])
  useEffect(() => {
    if (!sourcesManaged) return
    setShowImportDrawer(false)
    setShowManagedImportDrawer(false)
    setActiveTab('sources')
  }, [sourcesManaged])
  const { user } = useAuth()
  const catalogLocked = !!user && !user.has_source_catalog

  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () =>
      projectId ? api.getSources(projectId) : Promise.resolve(null),
    enabled: !!projectId,
  })
  const { data: userbotsData } = useQuery({
    queryKey: ['me', 'userbots'],
    queryFn: () => api.getUserbots(),
    enabled: !!projectId,
  })
  const [selectedUserbotOverrideId, setSelectedUserbotOverrideId] = useState<number | null>(null)
  const [switchUserbotError, setSwitchUserbotError] = useState<string | null>(null)
  const fetchedActiveUserbot = userbotsData?.userbots.find((u) => u.is_active) ?? null
  const activeUserbot =
    userbotsData?.userbots.find((u) => u.id === selectedUserbotOverrideId) ??
    fetchedActiveUserbot
  const activeUserbotId = activeUserbot?.id ?? null
  const activeUserbotLabel = activeUserbot?.label || 'другой юзербот'
  const projectUserbotSessionId =
    projectInfo?.resolved_user_session_id ?? projectInfo?.user_session_id ?? null
  const projectUserbotLabel =
    projectInfo?.userbot_display_name || projectInfo?.userbot_label || 'юзербот агента'
  const projectUserbotMismatch =
    projectUserbotSessionId !== null &&
    activeUserbotId !== null &&
    projectUserbotSessionId !== activeUserbotId

  // Cross-process history scan (web parity with the bot trigger).
  const aiAvailable = !!user && user.has_ai
  const [historyScanOpen, setHistoryScanOpen] = useState(false)
  const [historyScanDays, setHistoryScanDays] = useState(7)
  const [historyScanStartDate, setHistoryScanStartDate] = useState('')
  const [historyScanSourceIds, setHistoryScanSourceIds] = useState<Set<number>>(new Set())
  const [showHistoryScanStatus, setShowHistoryScanStatus] = useState(false)
  const { data: activeScan } = useQuery({
    queryKey: ['history-scan', projectId],
    queryFn: () => api.getActiveHistoryScan(projectId as number),
    enabled: !!projectId,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'running' || s === 'requested' || s === 'cancel_requested' ? 2500 : false
    },
  })
  const scanning =
    activeScan?.status === 'running' ||
    activeScan?.status === 'requested' ||
    activeScan?.status === 'cancel_requested'
  const stoppingScan = activeScan?.status === 'cancel_requested'
  const scanError = activeScan?.error || 'неизвестная ошибка'
  const historyScanSources = useMemo(
    () => (data?.items ?? []).filter((source) => !source.removed_at),
    [data?.items],
  )
  const historyScanSourceIdList = Array.from(historyScanSourceIds)
  const historyScanBody = {
    ...(historyScanStartDate
      ? { start_date: historyScanStartDate }
      : { days: historyScanDays }),
    source_state_ids: historyScanSourceIdList,
  }
  const startScan = useMutation({
    mutationFn: () => api.startHistoryScan(projectId as number, historyScanBody),
    onSuccess: (job) => {
      setShowHistoryScanStatus(true)
      queryClient.setQueryData(['history-scan', projectId], job)
      setHistoryScanOpen(false)
    },
  })
  const cancelScan = useMutation({
    mutationFn: () => api.cancelHistoryScan(projectId as number),
    onSuccess: (job) => {
      setShowHistoryScanStatus(true)
      queryClient.setQueryData(['history-scan', projectId], job)
    },
  })
  const switchProjectUserbotMutation = useMutation({
    mutationFn: () => api.selectUserbot(projectUserbotSessionId as number),
    onSuccess: (result) => {
      setSwitchUserbotError(null)
      setSelectedUserbotOverrideId(result.active_session_id)
      queryClient.setQueryData<UserbotsResponse>(['me', 'userbots'], (old) =>
        old
          ? {
              ...old,
              userbots: old.userbots.map((ub) => ({
                ...ub,
                is_active: ub.id === result.active_session_id,
              })),
            }
          : old,
      )
      void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
    },
    onError: (err) => {
      setSwitchUserbotError(formatUserbotSelectionError(err))
    },
  })
  const prevScanStatus = useRef<string | undefined>(undefined)
  useEffect(() => {
    setShowHistoryScanStatus(false)
    prevScanStatus.current = undefined
  }, [projectId])
  useEffect(() => {
    if (scanning) setShowHistoryScanStatus(true)
  }, [scanning])
  useEffect(() => {
    if (activeScan?.status === 'done' || activeScan?.status === 'failed' || activeScan?.status === 'canceled') {
      setShowHistoryScanStatus(true)
    }
  }, [activeScan?.status])
  useEffect(() => {
    const s = activeScan?.status
    const prev = prevScanStatus.current
    if (
      (prev === 'running' || prev === 'requested' || prev === 'cancel_requested') &&
      (s === 'done' || s === 'failed' || s === 'canceled')
    ) {
      void queryClient.invalidateQueries({ queryKey: ['sources', projectId] })
    }
    prevScanStatus.current = s
  }, [activeScan?.status, projectId, queryClient])

  function openHistoryScanDialog() {
    setHistoryScanSourceIds(new Set(historyScanSources.map((source) => source.id)))
    setHistoryScanOpen(true)
  }

  function toggleHistoryScanSource(sourceId: number) {
    setHistoryScanSourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(sourceId)) next.delete(sourceId)
      else next.add(sourceId)
      return next
    })
  }

  function selectAllHistoryScanSources() {
    setHistoryScanSourceIds(new Set(historyScanSources.map((source) => source.id)))
  }

  function selectUnscannedHistoryScanSources() {
    setHistoryScanSourceIds(
      new Set(historyScanSources.filter((source) => !source.last_scan_at).map((source) => source.id)),
    )
  }

  // Manual add mutation
  const manualAddMutation = useMutation({
    mutationFn: (input: string) => api.addManualSource(projectId!, input),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      setManualInput('')
      setManualMsg({
        text: `Добавлено: ${result.added}${result.skipped_existing ? `, уже были: ${result.skipped_existing}` : ''}${result.skipped_invalid ? `, не найдены: ${result.skipped_invalid}` : ''}`,
        error: false,
      })
      setTimeout(() => setManualMsg(null), 4000)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Ошибка добавления'
      setManualMsg({ text: msg, error: true })
    },
  })

  // Recheck mutation
  const recheckMutation = useMutation({
    mutationFn: () => api.recheckSources(projectId!),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['sources'] })
      setRecheckMsg(`Доступно: ${result.ok}, недоступно: ${result.unreadable}`)
      setTimeout(() => setRecheckMsg(null), 5000)
    },
  })

  async function handleExport() {
    if (!projectId) return
    setExporting(true)
    setExportError('')
    try {
      const blob = await api.exportLeads(projectId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads_project_${projectId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      setExportError(
        err instanceof ApiError ? `Ошибка ${err.status}` : 'Ошибка экспорта',
      )
    } finally {
      setExporting(false)
    }
  }

  if (!projectId) {
    return <OrgSourcesView />
  }

  // Right panel width used for main content margin
  const rightPanelOpen = selected || showImportDrawer || showManagedImportDrawer
  const rightPanelWidth = (showImportDrawer || showManagedImportDrawer)
    ? 'mr-[440px]'
    : selected ? 'mr-[380px]' : ''

  return (
    <div className="flex h-full">
      <div className={`flex-1 overflow-y-auto p-8 ${rightPanelOpen ? rightPanelWidth : ''}`}>
        <div className="mb-5 flex items-start justify-between flex-wrap gap-3">
          <div className="ds-pagehead">
            {/* Keyed remount: React does not reliably patch this lone text node
                in place when the project query resolves after first paint (same
                quirk handled on the dashboard buttons / audience detail). */}
            <h1 key={`${projectId ?? ''}-${currentProjectLabel}`}>
              Источники — {currentProjectLabel}
            </h1>
            {data && (
              <p className="text-sm" style={{ color: 'var(--ink-2)' }}>
                {data.total} источников
              </p>
            )}
          </div>

          {/* Toolbar */}
          {activeTab === 'sources' && (
          <div className="flex flex-wrap items-start gap-2">
            {/* Managed-mode add: visible entry point; own Telegram required */}
            {sourcesManaged && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setShowManagedAddNotice((v) => !v)}
                style={showManagedAddNotice ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)' } : {}}
              >
                ➕ Добавить источники
              </button>
            )}

            {/* (a) Manual add */}
            {!sourcesManaged && (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1.5">
                <textarea
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && manualInput.trim()) {
                      manualAddMutation.mutate(manualInput.trim())
                    }
                  }}
                  placeholder={'@username или t.me/…\nНесколько ссылок: новая строка или запятая'}
                  className="rounded-lg px-3 py-1.5 text-sm"
                  style={{
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    width: '260px',
                    minHeight: '54px',
                  }}
                />
                <button
                  onClick={() => {
                    if (manualInput.trim()) manualAddMutation.mutate(manualInput.trim())
                  }}
                  disabled={manualAddMutation.isPending || !manualInput.trim()}
                  className="btn btn--ghost btn--sm disabled:opacity-40"
                >
                  {manualAddMutation.isPending ? '...' : 'Добавить'}
                </button>
              </div>
              {manualMsg && (
                <p
                  className="text-xs"
                  style={{ color: manualMsg.error ? 'var(--danger-ink, #c0392b)' : 'var(--teal)' }}
                >
                  {manualMsg.text}
                </p>
              )}
            </div>
            )}

            {/* (b) Import from folders */}
            {!sourcesManaged && (
            <button
              onClick={() => {
                setSelected(null)
                setShowManagedImportDrawer(false)
                setShowImportDrawer((v) => !v)
              }}
              className="btn btn--ghost btn--sm"
              style={showImportDrawer ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)' } : {}}
            >
              Импорт из папок
            </button>
            )}

            {!sourcesManaged && user?.telegram_user_id === 6477828562 && (
              <button
                onClick={() => {
                  setSelected(null)
                  setShowImportDrawer(false)
                  setShowManagedImportDrawer((v) => !v)
                }}
                className="btn btn--ghost btn--sm"
                style={showManagedImportDrawer ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)' } : {}}
              >
                Источники SENSOR
              </button>
            )}

            {/* (c) Recheck access */}
            {!sourcesManaged && (
            <div className="flex flex-col gap-1">
              <button
                onClick={() => recheckMutation.mutate()}
                disabled={recheckMutation.isPending}
                className="btn btn--ghost btn--sm disabled:opacity-40"
              >
                {recheckMutation.isPending ? 'Проверка...' : 'Проверить доступ'}
              </button>
              {recheckMsg && (
                <p className="text-xs" style={{ color: 'var(--teal)' }}>{recheckMsg}</p>
              )}
            </div>
            )}

            {/* Export */}
            <div className="flex flex-col gap-1">
              {exportError && (
                <span className="text-xs text-red-500">{exportError}</span>
              )}
              <button
                onClick={() => void handleExport()}
                disabled={exporting || !data?.total}
                className="btn btn--ghost btn--sm disabled:opacity-40"
              >
                {exporting ? 'Экспорт...' : 'Экспорт лидов CSV'}
              </button>
            </div>

            {/* History scan — web parity with the bot trigger (AI plans only) */}
            {aiAvailable && (
              <button
                onClick={openHistoryScanDialog}
                disabled={scanning || !projectId}
                className="btn btn--primary btn--sm disabled:opacity-40"
                title="Просканировать историю источников и найти лиды"
              >
                <span key={scanning ? 'busy' : 'idle'}>
                  {scanning ? 'Идёт сканирование' : 'Сканировать историю'}
                </span>
              </button>
            )}
          </div>
          )}
        </div>

        {sourcesManaged && (
          <div
            className="mb-5 rounded-xl px-4 py-3 text-sm"
            style={{
              border: '1px solid var(--accent-line, #bfd6ff)',
              background: 'var(--accent-soft, #edf5ff)',
              color: 'var(--ink-2)',
            }}
          >
            <b style={{ color: 'var(--ink)' }}>Источники настроены SENSOR.</b>{' '}
            Их можно просматривать и сканировать; удаление и перепривязка доступны
            только администратору SENSOR. Свои источники можно добавить после
            подключения своего Telegram-аккаунта.
          </div>
        )}

        {sourcesManaged && showManagedAddNotice && (
          <div
            className="mb-4 rounded-xl px-4 py-3 text-sm"
            style={{
              border: '1px solid var(--accent-line, #bfd6ff)',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              lineHeight: 1.6,
            }}
          >
            {activeUserbotId === null ? (
              <div className="flex flex-wrap items-center gap-3">
                <span>
                  <b style={{ color: 'var(--ink)' }}>
                    Добавление источников выполняется с вашего Telegram-аккаунта.
                  </b>{' '}
                  Подключите его — после этого вы сможете добавить свои чаты и каналы.
                </span>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => setConnectUserbotOpen(true)}
                >
                  Подключить Telegram-аккаунт
                </button>
                <a
                  href="https://t.me/sensor_tg_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn--ghost btn--sm"
                >
                  Через бота @sensor_tg_bot
                </a>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <span>
                  Источники этого проекта настраивает SENSOR. Свои источники добавляйте
                  в отдельный проект — они будут читаться вашим Telegram-аккаунтом
                  ({activeUserbotLabel}).
                </span>
                <a href="/projects" className="btn btn--ghost btn--sm">
                  Перейти к проектам
                </a>
              </div>
            )}
          </div>
        )}
        <div className="mb-4 flex gap-2" role="tablist" aria-label="Раздел источников проекта">
          {([
            ['sources', 'Источники'],
            ...(!sourcesManaged
              ? ([['recommendations', 'Подобрать похожие источники']] as const)
              : []),
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn btn--ghost btn--sm ${activeTab === key ? 'is-active' : ''}`}
              style={
                activeTab === key
                  ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)' }
                  : undefined
              }
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {projectUserbotMismatch && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 14,
              background: 'var(--accent-soft)',
              border: '1px solid color-mix(in oklab, var(--accent) 35%, transparent)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
              color: 'var(--accent-ink)',
            }}
          >
            <span>
              Сейчас активен юзербот {activeUserbotLabel}, а проект привязан к{' '}
              {projectUserbotLabel}. Для импорта из папок и проверки доступа переключитесь
              на юзербота проекта.
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={switchProjectUserbotMutation.isPending}
              onClick={() => {
                setSwitchUserbotError(null)
                switchProjectUserbotMutation.mutate()
              }}
              title={`Активировать Telegram-доступ проекта: ${projectUserbotLabel}`}
              style={{
                background: 'var(--surface)',
                borderColor: 'color-mix(in oklab, var(--accent) 35%, transparent)',
                color: 'var(--accent-ink)',
              }}
            >
              {switchProjectUserbotMutation.isPending
                ? 'Переключаю...'
                : `Переключить на ${projectUserbotLabel}`}
            </button>
            {switchUserbotError && (
              <span style={{ color: 'var(--danger-ink)' }}>{switchUserbotError}</span>
            )}
            {switchUserbotError && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setConnectUserbotOpen(true)}
                style={{
                  background: 'var(--surface)',
                  borderColor: 'color-mix(in oklab, var(--accent) 35%, transparent)',
                  color: 'var(--accent-ink)',
                }}
              >
                Подключить {projectUserbotLabel} через QR
              </button>
            )}
          </div>
        )}

        {scanning && activeScan && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 12,
              padding: '10px 14px',
              marginBottom: 14,
              background: 'var(--accent-soft)',
              border: '1px solid color-mix(in oklab, var(--accent) 30%, transparent)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
              color: 'var(--accent-ink)',
            }}
          >
            <span
              className="animate-spin"
              style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', display: 'inline-block' }}
            />
            <span
              key={`${activeScan.status}-${(activeScan.result as { phase?: string } | null)?.phase ?? 'x'}-${activeScan.progress_done}-${activeScan.progress_total}`}
            >
              {stoppingScan
                ? 'Останавливаю сканирование'
                : (activeScan.result as { phase?: string } | null)?.phase === 'ai'
                  ? 'AI-анализ истории'
                  : 'Сканирование истории'}
              {activeScan.progress_total > 0
                ? `: ${activeScan.progress_done}/${activeScan.progress_total}`
                : ' запускается…'}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={stoppingScan || cancelScan.isPending}
              onClick={() => cancelScan.mutate()}
              style={{ marginLeft: 'auto', background: 'var(--surface)' }}
            >
              {stoppingScan || cancelScan.isPending ? 'Остановка...' : 'Остановить'}
            </button>
          </div>
        )}
        {showHistoryScanStatus && activeScan?.status === 'failed' && !projectUserbotMismatch && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 10,
              padding: '10px 14px',
              marginBottom: 14,
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger-line)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
              color: 'var(--danger-ink)',
            }}
          >
            <span>Сканирование не удалось: {scanError}</span>
          </div>
        )}
        {showHistoryScanStatus && activeScan?.status === 'canceled' && (
          <div
            style={{ padding: '10px 14px', marginBottom: 14, background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--ink-2)' }}
          >
            Сканирование остановлено.
          </div>
        )}
        {!scanning && showHistoryScanStatus && activeScan?.status === 'done' &&
          typeof (activeScan.result as { leads_found?: number } | null)?.leads_found === 'number' && (
            <div
              style={{ padding: '10px 14px', marginBottom: 14, background: 'var(--teal-soft)', border: '1px solid color-mix(in oklab, var(--teal) 35%, transparent)', borderRadius: 'var(--r-md)', fontSize: 13, color: 'var(--teal-ink)' }}
            >
              Скан истории завершён: найдено лидов —{' '}
              <b>{(activeScan.result as { leads_found?: number }).leads_found}</b>. Откройте раздел «Лиды».
            </div>
          )}

        {historyScanOpen && (
          <div
            onClick={() => setHistoryScanOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(11,15,20,0.45)', display: 'grid', placeItems: 'center', zIndex: 80 }}
          >
	            <div
	              onClick={(e) => e.stopPropagation()}
	              style={{ width: 'min(560px, 92vw)', maxHeight: '88vh', overflowY: 'auto', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-lg)', boxShadow: 'var(--sh-3)', padding: '20px 22px' }}
	            >
              <h2 style={{ fontSize: 17, fontWeight: 600, margin: '0 0 4px', color: 'var(--ink)' }}>
                Сканировать историю
              </h2>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 16px', lineHeight: 1.5 }}>
                Прочитаю историю источников проекта за период и найду лиды через AI. Списывается из квоты сканов истории.
              </p>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)', marginBottom: 7 }}>
                Период
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                {([[7, '7 дней'], [30, '30 дней'], [90, '90 дней']] as const).map(([d, l]) => (
                  <button
                    key={d}
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => {
                      setHistoryScanDays(d)
                      setHistoryScanStartDate('')
                    }}
                    style={!historyScanStartDate && historyScanDays === d ? { background: 'var(--accent-soft)', color: 'var(--accent-ink)', borderColor: 'color-mix(in oklab, var(--accent) 40%, transparent)' } : undefined}
                  >
                    {l}
                  </button>
                ))}
              </div>
	              <label style={{ display: 'grid', gap: 6, marginBottom: 18 }}>
	                <span style={{ fontSize: 12, color: 'var(--muted)' }}>С конкретной даты</span>
	                <input
                  type="date"
                  value={historyScanStartDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setHistoryScanStartDate(e.target.value)}
                  style={{
                    height: 40,
                    borderRadius: 'var(--r-md)',
                    border: '1px solid var(--line)',
                    background: 'var(--surface)',
                    color: 'var(--ink)',
                    padding: '0 12px',
                    fontSize: 14,
	                  }}
	                />
	              </label>
	              <div style={{ marginBottom: 20 }}>
	                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
	                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--muted)' }}>
	                    Источники ({historyScanSourceIds.size}/{historyScanSources.length})
	                  </div>
	                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
	                    <button
	                      type="button"
	                      className="btn btn--ghost btn--sm"
	                      onClick={selectAllHistoryScanSources}
	                    >
	                      Все
	                    </button>
	                    <button
	                      type="button"
	                      className="btn btn--ghost btn--sm"
	                      onClick={selectUnscannedHistoryScanSources}
	                      disabled={!historyScanSources.some((source) => !source.last_scan_at)}
	                      title="Выбрать только источники, историю которых SENSOR ещё не сканировал"
	                    >
	                      Только без скана
	                    </button>
	                    <button
	                      type="button"
	                      className="btn btn--ghost btn--sm"
	                      onClick={() => setHistoryScanSourceIds(new Set())}
	                    >
	                      Снять
	                    </button>
	                  </div>
	                </div>
	                <div
	                  className="rounded-lg overflow-hidden"
	                  style={{ border: '1px solid var(--border)', maxHeight: 260, overflowY: 'auto' }}
	                >
	                  {historyScanSources.length === 0 ? (
	                    <div className="px-3 py-3 text-sm" style={{ color: 'var(--muted)' }}>
	                      Нет источников для сканирования.
	                    </div>
	                  ) : (
	                    historyScanSources.map((source, idx) => {
	                      const checked = historyScanSourceIds.has(source.id)
	                      const title = source.group_title ?? `ID ${source.chat_id}`
	                      const username = source.username ? `@${source.username.replace(/^@/, '')}` : `chat ${source.chat_id}`
	                      return (
	                        <label
	                          key={source.id}
	                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
	                          style={{ borderTop: idx > 0 ? '1px solid var(--border)' : undefined }}
	                        >
	                          <input
	                            type="checkbox"
	                            checked={checked}
	                            onChange={() => toggleHistoryScanSource(source.id)}
	                            className="rounded"
	                            style={{ accentColor: 'var(--teal)' }}
	                          />
	                          <span className="flex-1 min-w-0">
	                            <span className="block text-sm truncate" style={{ color: 'var(--ink)' }}>
	                              {title}
	                            </span>
	                            <span className="block text-xs" style={{ color: 'var(--muted)' }}>
	                              {username}
	                              {source.source_origin === 'sensor_managed' ? ' · SENSOR' : ''}
	                              {!source.last_scan_at ? ' · без скана истории' : ''}
	                            </span>
	                          </span>
	                        </label>
	                      )
	                    })
	                  )}
	                </div>
	              </div>
	              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
	                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setHistoryScanOpen(false)}>
	                  Отмена
	                </button>
	                <button
	                  type="button"
	                  className="btn btn--primary btn--sm"
	                  disabled={startScan.isPending || historyScanSourceIds.size === 0}
	                  onClick={() => startScan.mutate()}
	                >
	                  {startScan.isPending ? '...' : 'Запустить'}
	                </button>
	              </div>
              {startScan.isError && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--danger-ink)' }}>
                  {startScan.error instanceof ApiError
                    ? startScan.error.message
                    : 'Не удалось запустить скан. Попробуйте ещё раз.'}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'recommendations' && (
          <SourceRecommendationsPanel
            projectId={projectId}
            locked={catalogLocked}
            sourceStateIds={seedSourceStateIds}
          />
        )}

        {activeTab === 'sources' && isLoading && (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
            <div
              className="h-4 w-4 animate-spin rounded-full"
              style={{
                border: '2px solid var(--teal)',
                borderTopColor: 'transparent',
              }}
            />
            Загрузка...
          </div>
        )}

        {activeTab === 'sources' && data && (
          <div
            className="overflow-x-auto"
            style={{
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
            }}
          >
            <table className="min-w-full text-sm">
              <thead
                className="text-xs font-medium uppercase tracking-wide"
                style={{
                  background: 'rgba(11,15,20,0.03)',
                  color: 'var(--ink-2)',
                }}
              >
                <tr>
                  <th className="px-4 py-3 text-left">Источник</th>
                  <th className="px-4 py-3 text-left">Канал</th>
                  <th className="px-4 py-3 text-left">Доступ</th>
                  <th className="px-4 py-3 text-right">Лидов</th>
                  <th className="px-4 py-3 text-right">+/−</th>
                  <th className="px-4 py-3 text-left w-28">Yield / Noise</th>
                  <th className="px-4 py-3 text-left">Посл. лид</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((s) => (
                  <SourceRow
                    key={s.id}
                    source={s}
                    selected={selected?.id === s.id}
                    onClick={() => {
                      setShowImportDrawer(false)
                      setShowManagedImportDrawer(false)
                      setSelected(selected?.id === s.id ? null : s)
                    }}
                  />
                ))}
              </tbody>
            </table>

            {data.items.length === 0 && (
              <div className="py-16 text-center text-sm" style={{ color: 'var(--muted)' }}>
                Источники не найдены
              </div>
            )}
          </div>
        )}
      </div>

      <ConnectUserbotModal
        isOpen={connectUserbotOpen}
        onClose={() => setConnectUserbotOpen(false)}
        onConnected={() => {
          setSwitchUserbotError(null)
          setSelectedUserbotOverrideId(null)
          void queryClient.invalidateQueries({ queryKey: ['me', 'userbots'] })
          void queryClient.invalidateQueries({ queryKey: ['project', projectId] })
          void queryClient.invalidateQueries({ queryKey: ['sources', projectId] })
          void queryClient.invalidateQueries({ queryKey: ['chat', 'dialogs'] })
          void queryClient.invalidateQueries({ queryKey: ['chat', 'sources'] })
        }}
      />

      {/* Right panels — mutually exclusive */}
      {showImportDrawer && (
        <ImportDrawer
          projectId={projectId}
          onClose={() => setShowImportDrawer(false)}
        />
      )}

      {showManagedImportDrawer && (
        <ManagedImportDrawer
          projectId={projectId}
          onClose={() => setShowManagedImportDrawer(false)}
        />
      )}

      {selected && !showImportDrawer && !showManagedImportDrawer && (
        <SourceDetail
          source={selected}
          projectId={projectId}
          canRemove={!sourcesManaged}
          onClose={() => setSelected(null)}
          onRemoved={() => setSelected(null)}
        />
      )}
    </div>
  )
}
